import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { prepareStayPlanContract } from "../plans/useContractStayPlan";
import { buildChargePeriod, type StayPlanContractDraft } from "../plans/stayPlanContractDraft";
import type { ComandaLine } from "./reservationForm";

export interface ContractPlanReservationInput {
  draft: StayPlanContractDraft;
  petId: string;
  ownerId: string | null;
  stayPackIds: string[];
  lines: ComandaLine[];
  /** A mensalidade do primeiro ciclo entra na comanda da guarda-chuva. */
  chargeInComanda: boolean;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function contractPlanReservation(
  input: ContractPlanReservationInput & { clinicId: string },
): Promise<{ contractId: string; reservationId: string; saleId: string }> {
  const { contractInsert, planInfo } = await prepareStayPlanContract({
    draft: input.draft,
    petId: input.petId,
    ownerId: input.ownerId,
    clinicId: input.clinicId,
  });

  const period = input.chargeInComanda
    ? buildChargePeriod({
        kind: planInfo.kind,
        startsOn: contractInsert.starts_on,
        firstDueOn: contractInsert.first_due_on,
        billingTiming: input.draft.billingTiming,
      })
    : null;

  const { data, error } = await (supabase as any).rpc("contract_daycare_plan_with_reservation", {
    p_contract: contractInsert,
    p_weekdays: input.draft.fixedWeekdays ?? [],
    p_pack_ids: input.stayPackIds,
    p_lines: input.lines
      .filter((line) => Boolean(line.itemName))
      .map((line) => ({ item_name: line.itemName, item_id: line.itemId ?? null, quantity: line.quantity, unit_price: line.unitPrice })),
    p_charge: period
      ? { due_on: period.dueOn, period_starts_on: period.periodStartsOn, period_ends_on: period.periodEndsOn }
      : null,
  });
  if (error) throw error;
  return { contractId: data.contract_id, reservationId: data.reservation_id, saleId: data.sale_id };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function useContractPlanReservation() {
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("daycareHotel");

  return useMutation({
    mutationFn: async (input: ContractPlanReservationInput) => {
      if (!profile?.current_clinic_id) throw new Error("no_clinic_selected");
      return contractPlanReservation({ ...input, clinicId: profile.current_clinic_id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts-for-reservation"] });
      queryClient.invalidateQueries({ queryKey: ["stay-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      toast({ title: t("reservationCreated") });
    },
    onError: () => {
      toast({ variant: "destructive", title: t("planContract.createFailed") });
    },
  });
}
